/**
 * useFieldSortState (Unit 5 Phase 1) — per-site sort levels for the field
 * records screen.
 *
 * THE SECOND persisted per-user UI preference in the field zone, and a
 * deliberate SIBLING of the first rather than a second storage mechanism.
 * useFieldHiddenFields owns the key (`toolsight-field-fsg-<siteId>`), the
 * schema version, the raw read and the merging write; this hook owns exactly
 * one top-level key inside that object:
 *
 *   { v: 1, hiddenFields: [...], sort: [ { fieldKey, direction }, ... ] }
 *
 * It imports readStored and persistPatch rather than restating them. A second
 * localStorage.getItem/setItem pair here is how the two writers eventually
 * drop each other's key — the exact failure useFieldHiddenFields:19-25 was
 * shaped to prevent.
 *
 * SCHEMA_VERSION IS 1 AND STAYS 1. `sort` is an additive sibling, not a new
 * shape. readSort gates on the same strict `!==` readHidden does, so a bump
 * would blank every rep's hidden columns AND their sort in the same breath.
 *
 * ORDER IS SIGNIFICANT: levels apply left to right, level 0 first. The array
 * IS the precedence.
 *
 * SANITIZE ON READ, NOT ONLY ON WRITE. A stored level naming a field that no
 * longer exists in any manifest is dropped before it can reach the
 * comparator — a rename or a removal upstream must degrade to "that level is
 * gone", never to a comparator reading undefined off every row and silently
 * ordering by nothing. Each surviving level is also rebuilt as a fresh
 * { fieldKey, direction } pair, so stray properties written by an older or
 * hand-edited build cannot ride along into state.
 *
 * NEVER THROWS — inherited whole from the storage layer. Every failure mode
 * (private browsing, quota, disabled storage, corrupt JSON, wrong version)
 * degrades to the same safe state: no sort, this session only.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { persistPatch, readStored } from './useFieldHiddenFields';
import {
  EDITABLE_FIELDS, READONLY_DISPLAY_FIELDS, DISPOSAL_ONLY_FIELDS,
} from '../data/editableFields';

const SCHEMA_VERSION = 1;

/**
 * ONE frozen empty array, not a fresh [] per call. Two reasons: an unchanged
 * empty sort keeps its identity across a re-read, so React bails out of the
 * render and the host's useMemo does not re-run; and sortRecords returns its
 * input by reference on an empty sort, so `displayedRecords === records`
 * stays true for the whole no-sort case.
 */
const EMPTY_SORT = Object.freeze([]);

/** 'asc' | 'desc' — the only two directions that survive sanitize. */
export const SORT_DIRECTIONS = Object.freeze(['asc', 'desc']);

/**
 * Every key a sort level may legally name, from the SAME manifests the record
 * modal and the Columns picker read.
 *
 * EDITABLE_FIELDS stands in for the module-private BASE_FIELDS. They are the
 * same 28 entries today — BASE_FIELDS ∩ SERVER_OWNED and
 * BASE_FIELDS ∩ CHILD_SENTINELS are both empty, so EDITABLE_FIELDS's filter
 * (editableFields.js) removes nothing — and where they could ever
 * diverge, EDITABLE_FIELDS is the conservative half: a key excluded there is
 * server-owned or a child-array sentinel, and neither is a scalar a rep should
 * be ordering rows by.
 *
 * DELIBERATELY WIDER THAN THE PICKER. The picker offers the current INV/ALL
 * view's fields only; this set is the union of every view, so switching INV↔ALL
 * cannot silently delete a rep's saved sort on an ALL-only field. Membership
 * here answers "does this field still exist", not "can it be chosen right now".
 */
export const SORTABLE_KEYS = new Set(
  [...EDITABLE_FIELDS, ...READONLY_DISPLAY_FIELDS, ...DISPOSAL_ONLY_FIELDS]
    .map((f) => f.key),
);

/**
 * Coerce any candidate list into clean sort levels. Drops an entry whose
 * fieldKey is not a live manifest key, and an entry whose direction is not
 * exactly 'asc' or 'desc'. Applied on READ as well as on write, for the same
 * reason useFieldHiddenFields.sanitize is.
 *
 * Duplicate fieldKeys are NOT collapsed. A repeated level is inert by
 * construction: the comparator is stable and its second visit to the same key
 * always returns 0, so a duplicate changes no row's position. Dropping it
 * would be a behaviour this phase was not asked for.
 */
function sanitizeSort(list) {
  if (!Array.isArray(list)) return EMPTY_SORT;
  const out = [];
  for (const entry of list) {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) continue;
    const { fieldKey, direction } = entry;
    if (typeof fieldKey !== 'string' || !SORTABLE_KEYS.has(fieldKey)) continue;
    if (direction !== 'asc' && direction !== 'desc') continue;
    // Rebuilt, never passed through — a stored level carrying extra keys
    // (an older build's dead "keepSorted", a hand edit) contributes only its
    // two meaningful fields.
    out.push({ fieldKey, direction });
  }
  return out.length ? out : EMPTY_SORT;
}

/**
 * The sort levels for a site. Empty for missing / malformed / wrong-v —
 * MIRRORS readHidden (useFieldHiddenFields.js:83-89) arm for arm, including
 * the strict `!==` on the version.
 */
function readSort(siteId) {
  const stored = readStored(siteId);
  if (!stored) return EMPTY_SORT;
  if (stored.v !== SCHEMA_VERSION) return EMPTY_SORT;
  if (!Array.isArray(stored.sort)) return EMPTY_SORT;
  return sanitizeSort(stored.sort);
}

export default function useFieldSortState(siteId) {
  const [sort, setSortState] = useState(() => readSort(siteId));

  // Re-read when the rep opens a different site. Ref guard identical to
  // useFieldHiddenFields:116-121 — it keeps mount from re-reading what the
  // lazy initialiser just read.
  const loadedFor = useRef(siteId);
  useEffect(() => {
    if (loadedFor.current === siteId) return;
    loadedFor.current = siteId;
    setSortState(readSort(siteId));
  }, [siteId]);

  // Both mutators are inert without a site: no state change, no write. A
  // write under a literal "toolsight-field-fsg-null" key would be a leak, not
  // a fallback — the same rule the hidden-fields mutators follow.
  const setSort = useCallback((next) => {
    if (siteId == null) return;
    const clean = sanitizeSort(next);
    setSortState(clean);
    persistPatch(siteId, { sort: clean });
  }, [siteId]);

  const clearSort = useCallback(() => {
    if (siteId == null) return;
    setSortState(EMPTY_SORT);
    persistPatch(siteId, { sort: [] });
  }, [siteId]);

  return { sort, setSort, clearSort };
}
